import parsePercent from './parsePercent';

/**
 * Tolerance for approximate numeric comparison of renderer position values.
 * Used to absorb floating-point noise from renderer round-trips
 * (e.g. "54%" echoed back as "53.99999999%").
 */
export const POSITION_EPSILON = 0.0001;

/**
 * Approximate equality check for two numeric values.
 * Non-finite values (NaN, Infinity) fall back to strict equality.
 */
export function isApproxEqual(
  a: number,
  b: number,
  epsilon: number = POSITION_EPSILON,
): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return a === b;
  }
  return Math.abs(a - b) <= epsilon;
}

/**
 * Approximate comparison of renderer source property values (strings like
 * "54%", raw numbers, or undefined). Parses both values via `parsePercent`
 * and compares with epsilon tolerance.
 *
 * This prevents false change-detection from renderer round-trip floating-point
 * noise (e.g. "54%" sent to renderer, echoed back as "53.99999999%").
 */
export function isSourceValueApproxEqual(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  epsilon: number = POSITION_EPSILON,
): boolean {
  // Fast path: strict equality handles identical strings and undefined/undefined
  if (a === b) return true;

  const parsedA = parsePercent(a);
  const parsedB = parsePercent(b);
  if (parsedA == null || parsedB == null) return false;
  return isApproxEqual(parsedA, parsedB, epsilon);
}
